import React, { Component } from 'react';

import {
  Badge,
  Flexbox,
  FontAwesomeIcon,
  WithPopover,
} from '@schoolinks/ui-components';
import { isEqual, noop } from 'lodash';
import { number, func, shape, bool } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';

import withUserType from 'containers/withUserType';
import features from 'core/features';

import * as notificationActions from 'modules/Notifications/actions';
import NotificationsContainer from 'modules/Notifications/components/NotificationsContainer';
import {
  DEFAULT_NOTIFICATION_INTERVAL,
  K12_ADMIN_NOTIFICATION_INTERVAL,
} from 'modules/Notifications/constants';

import s from './Notifications.css';

const PER_PAGE = 5;

@withUserType
@connect(
  ({
    notifications: {
      page,
      notifications,
      unreadCount,
      unseenCount,
      downloadUrl,
      downloadNotificationId,
    },
  }) => ({
    page,
    notifications,
    unreadCount,
    unseenCount,
    downloadUrl,
    downloadNotificationId,
  }),
  dispatch => bindActionCreators({ ...notificationActions }, dispatch),
)
export default class Notifications extends Component {
  async componentDidMount() {
    const { user } = this.props;
    if (!__DEV__ || features.polling) {
      const intervalTime = user?.isCounselor
        ? K12_ADMIN_NOTIFICATION_INTERVAL
        : DEFAULT_NOTIFICATION_INTERVAL;
      this.interval = setInterval(
        () => this.getLatestUnseenCount(),
        intervalTime,
      );
    }
  }

  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  getLatestUnseenCount = async () => {
    const isOnboardingPage = (window?.location?.href ?? '').includes(
      '/onboarding/student',
    );

    // don't send a request during student onboarding or if the browser tab is inactive
    // if users use more than 1 tab this can significantly reduce the number of requests
    if (isOnboardingPage || document.hidden) {
      return;
    }

    const { unseenCount, getUnseenNotificationsCount } = this.props;
    try {
      const { data: { unseenNotificationsCount: count } = {} } =
        await getUnseenNotificationsCount();
      if ((count || count === 0) && count > unseenCount)
        await this.getLatestNotifications();
    } catch (e) {
      console.warn('could not fetch latest count', e);
    }
  };

  getLatestNotifications = async () => {
    const { getNotifications, getUnreadNotificationsCount } = this.props;
    await getUnreadNotificationsCount();
    await getNotifications(
      {
        limit: PER_PAGE,
      },
      true,
    );
  };

  getNotifications = async () => {
    const {
      page,
      getNotifications,
      getUnreadNotificationsCount,
      getUnseenNotificationsCount,
    } = this.props;
    await Promise.all([
      getUnseenNotificationsCount(),
      getUnreadNotificationsCount(),
      getNotifications({
        limit: PER_PAGE,
        offset: page * PER_PAGE,
      }),
    ]);
  };

  handleAllNotificationsSeen = async () => {
    const { unseenCount, markAllNotificationSeen } = this.props;
    if (unseenCount > 0) {
      await markAllNotificationSeen();
    }
  };

  render() {
    const {
      unreadCount,
      unseenCount,
      markNotificationRead,
      markAllNotificationRead,
      markAllNotificationSeen,
      onClick,
    } = this.props;

    return (
      <WithPopover
        aria-label="Notifications"
        paperClass={s.paper}
        hideArrow
        popoverContent={() => (
          <NotificationsContainer
            unreadCount={unreadCount}
            getInitialNotifications={this.getNotifications}
            getNextNotifications={this.getNotifications}
            markNotificationRead={markNotificationRead}
            markAllNotificationSeen={markAllNotificationSeen}
            markAllNotificationRead={markAllNotificationRead}
          />
        )}
        anchorContent={() => (
          <Flexbox className={s.popoverButton}>
            <FontAwesomeIcon icon={faBell} color="white" />
            {unseenCount > 0 ? (
              <Badge
                number={unseenCount > 99 ? '99+' : unseenCount}
                color="red"
                classes={{ root: s.badge }}
              />
            ) : null}
          </Flexbox>
        )}
        onAnchorClick={onClick}
        onCloseCallback={this.handleAllNotificationsSeen}
        tooltipProps={{
          title: 'View Notifications',
        }}
      />
    );
  }
}

Notifications.propTypes = {
  unseenCount: number,
  unreadCount: number,
  page: number,
  getNotifications: func,
  getUnreadNotificationsCount: func,
  getUnseenNotificationsCount: func,
  markNotificationRead: func,
  markAllNotificationRead: func,
  markAllNotificationSeen: func,
  user: shape({
    isCounselor: bool,
  }),
  onClick: func,
};

Notifications.defaultProps = {
  unseenCount: 0,
  unreadCount: 0,
  user: {},
  markAllNotificationSeen: undefined,
  markAllNotificationRead: undefined,
  markNotificationRead: undefined,
  getUnseenNotificationsCount: undefined,
  getUnreadNotificationsCount: undefined,
  getNotifications: undefined,
  page: undefined,
  onClick: noop,
};
